<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';

import FormSummaryComments from '@/components/ConsultationSummary/FormSummaryComments.vue';
import FormSummaryResults from '@/components/ConsultationSummary/FormSummaryResults.vue';

import type { SummaryFormDto, Locales, SummaryExportingOptions } from '@/services/types/api.types';
import { getTranslation } from '@/composables/data-display-utils';
import { useSummariesStore } from '@/stores/summaries.store';
import { Constants } from '@/constants';

// PROPS
const props = defineProps<{
  languageEnabled: Locales[];
  form: SummaryFormDto;
}>();

// EMITS
const emit = defineEmits<{
  (e: 'onExportingSummary', options: SummaryExportingOptions): void;
}>();

// CONST
const { t } = useI18n();

// STORE
const summariesStore = useSummariesStore();
const { formComments } = storeToRefs(summariesStore);

// FUNCTIONS
const fetchFormData = async () => {
  if (props.form.are_comments_enabled && !formComments.value?.has(props.form.id)) {
    await summariesStore.getFormComments(props.form.id);
  }
  await summariesStore.getFormStats(props.form.id);
};

// COMPUTED
const showComments = computed(
  () => ![Constants.Form.Type.Map, Constants.Form.Type.OpenQuestion].includes(props.form.type),
);

const onExportingSummary = () => emit('onExportingSummary', { formId: props.form.id });
</script>
<template>
  <div class="form-details-card__wrapper">
    <q-list class="rounded-borders">
      <q-expansion-item @show="fetchFormData">
        <template v-slot:header>
          <div class="form-details-card">
            <div class="form-details-card__image">
              <div v-if="props.form.image?.url" class="form-details-card__image-form">
                <img :src="props.form.image.url" alt="form image" />
              </div>
              <div v-else class="form-details-card__image-fallback">
                <q-icon name="o_article" />
              </div>
            </div>
            <div class="form-details-card__content">
              <div class="form-details-card__content-title">
                {{ getTranslation(props.form, 'title', props.languageEnabled) }}
              </div>
              <div class="form-details-card__content-details">
                {{ props.form.number_of_participants }}
                {{ t('form.summary.total-votes', props.form.number_of_participants) }} ({{
                  Math.round(props.form.participation_rate)
                }}%)
              </div>
            </div>
          </div>
          <div class="form-details-menu">
            <q-btn icon="more_horiz" flat dense @click.stop>
              <q-menu class="q-actions-menu">
                <q-list>
                  <q-item clickable @click="onExportingSummary" v-close-popup>
                    <q-item-section>{{ t('global.export') }}</q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </q-btn>
          </div>
        </template>
        <q-card>
          <q-card-section>
            <div class="form-summary-content">
              <form-summary-results :form-id="props.form.id" :language-enabled="props.languageEnabled" />
              <q-separator v-if="showComments" />
              <form-summary-comments
                v-if="showComments"
                :form-id="props.form.id"
                :are-comments-enabled="props.form.are_comments_enabled"
              />
            </div>
          </q-card-section>
        </q-card>
      </q-expansion-item>
    </q-list>
  </div>
</template>
<style lang="scss">
.form-details-card__wrapper {
  width: 100%;
  margin-bottom: $space-lg;

  .q-expansion-item {
    width: inherit;

    .q-item {
      width: 100%;
    }
  }

  .q-item__section {
    .q-icon {
      color: $color-neutral-black !important;
    }
  }

  .q-card__section {
    padding-top: 0;
  }

  .form-details-card {
    display: flex;
    flex-direction: row;
    gap: $space-md;
    padding: $space-lg $space-md;
    width: inherit;
    color: $color-neutral-black-text;

    &__image {
      &-fallback {
        display: flex;
        justify-content: center;
        align-items: center;
        background: $color-neutral-grey-300;
        border-radius: 6px;
        height: 80px;
        width: 80px;

        .q-icon {
          font-size: 42px;
          color: $color-neutral-black-text !important;
        }
      }

      &-form {
        height: 80px;
        width: 80px;

        img {
          border-radius: 6px;
          height: 80px;
          width: 80px;
          object-fit: cover;
        }
      }
    }

    &__content {
      display: flex;
      flex-direction: column;

      &-title {
        font-weight: bold;
        color: $color-neutral-black;
      }

      &-details {
        font-size: $small-text;
      }
    }
  }

  .form-summary-content {
    display: flex;
    flex-direction: column;
    gap: $space-md;

    .q-separator {
      margin: 0 $space-md;
    }
  }

  .form-details-menu {
    align-content: center;
    button {
      color: $color-neutral-black;
    }
  }
}
.form-details-card__wrapper > .q-list > .q-expansion-item--expanded > .q-expansion-item__container > .q-item {
  border-radius: 6px 6px 0 0 !important;
  overflow: hidden;
  background-color: $color-neutral-white;
}

.form-details-card__wrapper > .q-list > .q-expansion-item--collapsed > .q-expansion-item__container > .q-item {
  overflow: hidden;
  background-color: $color-neutral-white;
  border-radius: 6px !important;
}

.form-details-card__wrapper
  > .q-list
  > .q-expansion-item--expanded
  > .q-expansion-item__container
  > .q-expansion-item__content {
  overflow: hidden;
  background-color: $color-neutral-white;
  border-radius: 0 0 6px 6px !important;
}
</style>
